import type { ReactNode } from "react";
import styles from "./VisuallyHidden.module.css";

export interface VisuallyHiddenProps {
  readonly children: ReactNode;
  /**
   * Render as something other than a `span` when the hidden text has to sit
   * inside an element that rejects phrasing content — a `<tr>`'s only legal
   * children are cells, for instance.
   */
  readonly as?: "span" | "div" | "p";
  readonly id?: string;
  readonly testId?: string;
}

/**
 * Text that is present for assistive technology and absent for sighted users.
 *
 * Used for the spoken half of every §8.4 redundancy pair: the word beside a
 * selection glyph, the "Power 2 of 3, Sharp" beside three dots, the distinct
 * label beside an outcome dot. It is deliberately not an `aria-label` helper —
 * `aria-label` replaces the visible text for AT, while this adds to it, and the
 * redundancy rules are additive.
 */
export function VisuallyHidden({
  children,
  as = "span",
  id,
  testId,
}: VisuallyHiddenProps) {
  const Element = as;
  return (
    <Element className={styles.hidden} id={id} data-testid={testId}>
      {children}
    </Element>
  );
}
